import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { CORE_QUERY_KEYS } from 'app/hooks/data/core/admin/experiences';
import {
  mapExperienceToCreateRequest,
  mapExperienceToUpdateRequest,
} from 'app/hooks/data/core/admin/experiences/mapping/form';
import { useCreateExperience } from 'app/hooks/data/core/admin/experiences/useCreateExperience';
import { useDeleteExperience } from 'app/hooks/data/core/admin/experiences/useDeleteExperience';
import { useUpdateExperience } from 'app/hooks/data/core/admin/experiences/useUpdateExperience';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import type { ExperienceDurationDescription } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import { dayjs } from 'app/utils/dayjs';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';
import { pluralize } from 'app/utils/strings';

import {
  activeTabHasFieldError,
  getFirstActiveTabErrorField,
  getRejectedFields,
} from '../experienceTabFields';
import { MenuItemName } from '../menuItem';

import type { ExperienceInitialFormType } from './useExperienceData';
import {
  resetFormDirty,
  resetValidationErrors,
  setStockValidationError,
  setValidationErrors,
  validateAllConfigurations,
} from './useValidationStore';

// Availability validation errors (stock/price/guests) are custom spans, not antd
// fields, so form.scrollToField can't reach them. Scroll to the first rendered
// error message on the next frame, once React has committed the error state.
const scrollToFirstCustomError = (): void => {
  requestAnimationFrame(() => {
    document
      .querySelector('.experience-content-form .form-error-message')
      ?.scrollIntoView(SCROLL_TO_FIRST_ERROR);
  });
};

const cleanExperienceValues = <T extends Partial<ExperienceInitialFormType>>(
  values: T
) => ({
  ...values,
  closedDaysBeforeCheckIn: values.lastMinuteRule
    ? values.closedDaysBeforeCheckIn
    : null,
  maxQuantity: values.maxAddonQuantity ? values.maxQuantity : null,
  spaceIds:
    values.spaceIds?.filter((id) => id !== NOT_APPLICABLE_SPACE_ID) || [],
  time: values.time ? dayjs(values.time).format('HH:mm') : undefined,
  isTimeslotDaytimeOnly:
    values.availabilityMode === AVAILABILITY_MODE.TIMESLOT
      ? (values.isTimeslotDaytimeOnly ?? true)
      : undefined,
});

type UseExperienceActionsProps = {
  experienceId?: number;
  isCreatePage: boolean;
  initialValues?: Partial<ExperienceInitialFormType>;
  activeTab: MenuItemName;
};

export const useExperienceActions = ({
  experienceId,
  isCreatePage,
  initialValues,
  activeTab,
}: UseExperienceActionsProps) => {
  const [form] = Form.useForm<ExperienceInitialFormType>();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: createExperience } = useCreateExperience();
  const { mutateAsync: updateExperience } = useUpdateExperience();
  const { mutateAsync: deleteExperience } = useDeleteExperience();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDurationModal, setShowDurationModal] = useState<boolean>(false);

  const onSavedRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  const formatDuration = (
    duration?: number,
    durationDescription?: ExperienceDurationDescription
  ): string | null => {
    if (!duration || !durationDescription) {
      return null;
    }

    const durationMap: Record<ExperienceDurationDescription, string> = {
      MINUTE: pluralize(duration, 'min', 'mins'),
      HOUR: pluralize(duration, 'hour', 'hours'),
      HALF_DAY: 'half day',
      DAY: pluralize(duration, 'day', 'days'),
    };

    return durationMap[durationDescription];
  };

  const formatName = useCallback(
    (experience: ExperienceInitialFormType, categoryName?: string): string => {
      const parts: string[] = [];

      if (categoryName) {
        parts.push(categoryName);
      }

      if (experience.location) {
        parts.push(experience.location);
      }

      const duration = formatDuration(
        experience.duration,
        experience.durationDescription
      );

      if (duration) {
        parts.push(duration);
      }

      return parts.join(' • ');
    },
    []
  );

  const onFinish = useCallback(
    async (values: ExperienceInitialFormType) => {
      const allValues = {
        ...form.getFieldsValue(true),
        ...values,
      } as ExperienceInitialFormType;

      const cleanedValues = cleanExperienceValues(allValues);

      try {
        if (isCreatePage) {
          const createdExperience = await createExperience(
            mapExperienceToCreateRequest(cleanedValues)
          );

          const location = {
            pathname: `/activities/${createdExperience?.id}`,
          };

          history.push(location);
        } else if (experienceId) {
          const payload = mapExperienceToUpdateRequest(
            cleanedValues,
            initialValues && cleanExperienceValues(initialValues)
          );

          if (Object.keys(payload).length === 0) {
            enterViewMode();

            return;
          }

          await updateExperience({
            id: experienceId,
            payload,
          });

          queryClient.invalidateQueries({
            queryKey: [
              CORE_QUERY_KEYS.experience,
              { id: experienceId.toString() },
            ],
          });

          enterViewMode();
        } else {
          return;
        }

        resetFormDirty();
      } finally {
        setIsLoading(false);
      }
    },
    [
      createExperience,
      enterViewMode,
      experienceId,
      form,
      history,
      initialValues,
      isCreatePage,
      queryClient,
      updateExperience,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    resetValidationErrors();
    resetFormDirty();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (experienceId) {
      await deleteExperience(experienceId);
      history.push('/activities');
    }
  }, [deleteExperience, experienceId, history]);

  const submitForm = useCallback(async (): Promise<'saved' | 'failed'> => {
    setStockValidationError({ isValid: true, errors: [] });
    setIsLoading(true);

    const onSaved = onSavedRef.current;

    onSavedRef.current = null;

    try {
      await onFinish(form.getFieldsValue(true));
      onSaved?.();

      return 'saved';
    } catch {
      setIsLoading(false);

      return 'failed';
    }
  }, [form, onFinish]);

  const hasDurationChanged = useCallback(() => {
    if (!initialValues) {
      return false;
    }

    const values = form.getFieldsValue(true);

    return (
      values?.duration !== initialValues.duration ||
      values?.durationDescription !== initialValues.durationDescription
    );
  }, [form, initialValues]);

  const hadActiveTimeSlots = useCallback(
    () => initialValues?.availabilityMode === AVAILABILITY_MODE.TIMESLOT,
    [initialValues]
  );

  const saveActiveTab = useCallback(
    async (
      onSaved?: () => void
    ): Promise<'saved' | 'invalid' | 'failed' | 'deferred'> => {
      try {
        await form.validateFields();
      } catch (error) {
        const rejectedFields = getRejectedFields(error);

        const blocks = isCreatePage
          ? rejectedFields.length > 0
          : activeTabHasFieldError(rejectedFields, activeTab);

        if (blocks) {
          setCanSubmit(false);

          const firstErrorField = isCreatePage
            ? rejectedFields[0]?.name
            : getFirstActiveTabErrorField(rejectedFields, activeTab);

          if (firstErrorField) {
            form.scrollToField(firstErrorField, SCROLL_TO_FIRST_ERROR);
          }

          return 'invalid';
        }
      }

      if (activeTab === MenuItemName.Availability) {
        const { stockError, priceError, guestsError } =
          validateAllConfigurations(form.getFieldsValue(true));

        if (
          !stockError.isValid ||
          !priceError.isValid ||
          !guestsError.isValid
        ) {
          setValidationErrors({ stockError, priceError, guestsError });
          setCanSubmit(false);
          scrollToFirstCustomError();

          return 'invalid';
        }
      }

      onSavedRef.current = onSaved ?? null;

      if (!isCreatePage && hasDurationChanged() && hadActiveTimeSlots()) {
        setShowDurationModal(true);

        return 'deferred';
      }

      return submitForm();
    },
    [
      form,
      activeTab,
      isCreatePage,
      hasDurationChanged,
      hadActiveTimeSlots,
      submitForm,
    ]
  );

  const handleSave = useCallback(() => {
    void saveActiveTab();
  }, [saveActiveTab]);

  const handleDurationModalConfirm = useCallback(() => {
    setShowDurationModal(false);
    void submitForm();
  }, [submitForm]);

  const handleDurationModalCancel = useCallback(() => {
    onSavedRef.current = null;
    setShowDurationModal(false);
  }, []);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    saveActiveTab,
    enterEditMode,
    enterViewMode,
    setCanSubmit,
    formatName,
    showDurationModal,
    handleDurationModalConfirm,
    handleDurationModalCancel,
  };
};
